import { ENVIRONMENT } from '../environment/environment.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Environment, Event, EventDetails, Response } from '@viso/core/models';
import { Inject, Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { DateService } from '@viso/core/utilities';
import { Socket } from 'ngx-socket-io';

@Injectable({
  providedIn: 'root',
})
export class EventService {
  constructor(
    private http: HttpClient,
    private dateHelper: DateService,
    private socket: Socket,
    @Inject(ENVIRONMENT) private environment: Environment
  ) {}

  getPastEventList() {
    return this.http.get(
      `${this.environment.API_URL}/client/event?page=0&type=past`
    );
  }

  getCurrentEventList() {
    return this.http.get(
      `${this.environment.API_URL}/client/event?page=0&type=current`
    );
  }

  convertToIonDatetime(dateString) {
    const date = new Date(dateString);
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    const timezoneOffset = -date.getTimezoneOffset();
    const timezoneHours = String(Math.floor(Math.abs(timezoneOffset) / 60)).padStart(2, '0');
    const timezoneMinutes = String(Math.abs(timezoneOffset) % 60).padStart(2, '0');
    const timezoneSign = timezoneOffset >= 0 ? '+' : '-';
    
    const ionDatetime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezoneSign}${timezoneHours}:${timezoneMinutes}`;
    
    return ionDatetime;
  }

  transformEventGet(event: Event) {
    event.event_date = this.convertToIonDatetime(this.dateHelper.convertDateFromYYYYmmDD(event.event_date));
    console.log(this.dateHelper.extractTimeToDate(event.start_time));
    event.start_time = this.convertToIonDatetime(this.dateHelper.extractTimeToDate(event.start_time));
    event.services.forEach(
      e => e.expected_time = this.convertToIonDatetime(this.dateHelper.extractTimeToDate(e.expected_time))
    );
    event.other_services.forEach(
      e => e.expected_time = this.convertToIonDatetime(this.dateHelper.extractTimeToDate(e.expected_time))
    );
    return event;
  }

  transformEventSave(event: Event) {
    event.event_date = this.dateHelper.convertDateToYYYYmmDD(event.event_date);
    event.start_time = this.dateHelper.extractTimeFromDate(event.start_time);
    if(typeof(event.state_id.id) == 'undefined'){
      event.state_id = event.state_id;
    }
    else{
      event.state_id = event.state_id.id;
    }
    
    event.services.forEach(
      e => {
        e.expected_time = this.dateHelper.extractTimeFromDate(e.expected_time || e.expected_time);
        e.expected_event_date = e.expected_event_date ? 
          this.dateHelper.convertDateToYYYYmmDD(e.expected_event_date):
          event.event_date;
        // set service to 0 if event type is other 
        if (e.type === 'other') {
          e.service_id = 0;
        }
      }
    );
    return event;
  }

  getEventById(id: string) {
    return this.http.get<Response<Event>>(
      `${this.environment.API_URL}/client/getEventDetails/${id}`
    ).pipe(
      map(e => this.transformEventGet(e.body))
    );
  }

  createEvent(event: Event) {
    event = this.transformEventSave(event);
    return this.http.post<Response<Event>>(`${this.environment.API_URL}/client/event`, event);
  }

  eventCreated(event){
    this.socket.emit('event_created', {
      event: event
    });
  }

  updateEvent(id: string, event: Event) {
    event = this.transformEventSave(event);
    return this.http.put<Response<Event>>(`${this.environment.API_URL}/client/event/${id}`, event);
  }

  deleteEventService(id: string) {
    return this.http.delete<Response<Event>>(`${this.environment.API_URL}/client/deleteService/${id}`);
  }

  getEvent(id: string) {
    return this.http.get<Response<EventDetails>>(`${this.environment.API_URL}/client/event-details/${id}`);
  }

  deleteEvent(eventId: string) {
    return this.http.delete(
      `${this.environment.API_URL}/client/event/${eventId}`
    );
  }

  getEventListForVendor(params: HttpParams) {
    return this.http.get(`${this.environment.API_URL}/vendor/event`, {
      params,
    });
  }

  getEventDetails(eventId) {
    return this.http.get(`${this.environment.API_URL}/vendor/event/${eventId}`);
  }

  getServiceLocationForVendor() {
    return this.http
      .get(`${this.environment.API_URL}/vendor/service-location-list`)
      .pipe(map((res: Response<any>) => res.body));
  }

  bookmarkEvent(event_id: string, type: string) {
    return this.http
      .post(`${this.environment.API_URL}/vendor/save-event`,
        { event_id, type }
      )
      .pipe(map((res: Response<any>) => res.body));
  }

  markEventAsRead(eventId: string, userId: string) {
    return this.http.post<Response<any>>(
      `${this.environment.API_URL}/vendor/mark-read-event/${eventId}/${userId}`,
      null
    ).pipe(
      map((res: Response<any>) => res.body)
    );
  }
  
  getClientEvents(page: number, type) {
    return this.http.get<Response<any>>(`${this.environment.API_URL}/client/event?page=${page}&type=${type}`);
  }

  getClientEventDetails(uuid: string) {
    return this.http.get<Response<any>>(`${this.environment.API_URL}/client/event-details/${uuid}`);
  }

  cancelEvent(uuid: string, reason: string) {
    return this.http.put<Response<any>>(`${this.environment.API_URL}/client/cancel-event/${uuid}`, { reason });
  }

  restoreEvent(uuid: string) {
    return this.http.put<Response<any>>(`${this.environment.API_URL}/client/restore-event`, { event_id: uuid });
  }

  restoreAllEvent() {
    return this.http.post<Response<any>>(`${this.environment.API_URL}/client/restore-all-events`, null);
  }

  hireOrWithdrawnEventService(data: {uuid: string, 'hired': boolean, 'reason': string}) {
    return this.http.put<Response<any>>(`${this.environment.API_URL}/client/hire-for-service/${data.uuid}`, data);
  }

  getReasonsForCancel(code: string) {
    return this.http.get<Response<any>>(`${this.environment.API_URL}/reason/${code}`);
  }
}
